import { useState, useCallback } from 'react';
import type { ProcedureType } from '../../../../../../services/ConfigurationService/types';

export interface ScheduledProcedureCodeFormData {
  code: string;
  description: string;
}

const createInitialFormData = (): ScheduledProcedureCodeFormData => ({
  code: '',
  description: '',
});

export const useScheduledProcedureCodeAddForm = () => {
  const [formData, setFormData] = useState<ScheduledProcedureCodeFormData>(createInitialFormData());
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureType | null>(null);
  const [error, setError] = useState('');

  const handleInputChange = useCallback((field: keyof ScheduledProcedureCodeFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  }, [error]);

  const handleProcedureChange = useCallback((procedure: ProcedureType | null) => {
    setSelectedProcedure(procedure);
    if (procedure) {
      setFormData({
        code: procedure.code,
        description: procedure.description,
      });
    } else {
      setFormData(createInitialFormData());
    }
    // Clear error when user makes a selection
    if (error) setError('');
  }, [error]);

  const validateForm = useCallback((): boolean => {
    if (!selectedProcedure) {
      setError('Please select a procedure from the dropdown');
      return false;
    }
    return true;
  }, [selectedProcedure]);

  const resetForm = useCallback(() => {
    setFormData(createInitialFormData());
    setSelectedProcedure(null);
    setError('');
  }, []);

  return {
    formData,
    selectedProcedure,
    error,
    setError,
    handleInputChange,
    handleProcedureChange,
    validateForm,
    resetForm,
  };
};
